import * as React from 'react';
import { css } from '@emotion/react';
import { useState } from 'react';
import { TextInput, Select, tokens } from '@hotjar/hotsauce';
import { useForm, Controller } from 'react-hook-form';

import { useTranslation } from 'react-i18next';
import { GoogleSSOButton } from '@hotjar/webapp/components/SSOButton';
import { TextHorizontalRule } from '@hotjar/webapp/components/TextHorizontalRule';
import { PasswordInputWithValidation } from '@hotjar/webapp/components/PasswordInputWithValidation';
import { useRoleChoices } from '@hotjar/webapp/hooks/useRoleChoices';

import { ErrorText } from '@hotjar/webapp/components/ErrorText';
import { StyledButton, StyledCheckbox, Title, SubTitle, Caption, StyledText } from './styles';
import {
  ColFormWrapper,
  Form,
  StyledField,
  StyledAlert,
  StyledErrorText,
  TermsOfServiceLink,
} from '../../../common/styles';

export type InviteRegisterFormProps = {
  onGoogleBtnClick: () => void;
  onSubmitBtnClick: (data: InviteRegisterFormData) => void;
  organizationName: string;
  email: string;
  name: string;
  loggedInAs: string;
  isAuthenticatedWithGoogle: boolean;
  isGoogleSsoError: boolean;
};

type InviteRegisterFormData = {
  name: string;
  role: string;
  terms: boolean;
  password: string;
};

export const InviteRegisterForm = ({
  onGoogleBtnClick,
  onSubmitBtnClick,
  organizationName,
  email,
  name,
  loggedInAs,
  isAuthenticatedWithGoogle,
  isGoogleSsoError,
}: InviteRegisterFormProps): JSX.Element => {
  const { t } = useTranslation();
  const [isSubmitInProgress, setSubmitInProgress] = useState(false);
  const [isPasswordValid, setPasswordValidState] = useState(true);
  const roles = useRoleChoices();
  const { register, control, handleSubmit, errors, formState } = useForm<InviteRegisterFormData>();

  const onSubmit = (formData: InviteRegisterFormData) => {
    const submitValues = {
      ...formData,
      role: formData.role,
    };
    setSubmitInProgress(true);
    if (isAuthenticatedWithGoogle) {
      onSubmitBtnClick({
        ...submitValues,
        name,
        password: '',
      });
    } else {
      onSubmitBtnClick(submitValues);
    }
  };

  return (
    <ColFormWrapper>
      <Form
        id="join-team"
        onSubmit={handleSubmit(onSubmit)}
        data-testid="join-team-form"
        noValidate
      >
        <Title css={isAuthenticatedWithGoogle && { 'margin-top': tokens.spacer.x1 }}>
          {t('common:registration.from-invite.welcome')}
        </Title>
        <SubTitle>{t('common:registration.from-invite.subtitle')}</SubTitle>
        {!!loggedInAs && (
          <StyledAlert kind="warning">
            {t('common:registration.from-invite.logged-in-alert', { loggedInAs, email })}
          </StyledAlert>
        )}
        <Caption>{t('common:registration.from-invite.organization-caption')}</Caption>
        <StyledText textType="subtitle2">{organizationName}</StyledText>
        <Caption>{t('common:registration.from-invite.email-caption')}</Caption>
        <StyledText textType="subtitle2">{email}</StyledText>
        {isAuthenticatedWithGoogle ? (
          <>
            <Caption>{t('common:registration.select-signup-method.name-field-label')}</Caption>
            <StyledText textType="subtitle2">{name}</StyledText>
          </>
        ) : (
          <>
            <GoogleSSOButton
              id="google-button"
              data-testid="google-button"
              containerStyleProps={css`
                margin-top: ${tokens.spacer.x3};
              `}
              onClick={onGoogleBtnClick}
            >
              {t('common:registration.select-signup-method.google-signup')}
            </GoogleSSOButton>
            {isGoogleSsoError && (
              <ErrorText data-testid="google-sso-error">
                {t('common:registration.from-invite.google-sso-error')}
              </ErrorText>
            )}
            <TextHorizontalRule
              message={t('common:registration.from-invite.horizontal-rule-msg')}
              css={css`
                margin: ${tokens.spacer.x4} 0;
              `}
            />

            <StyledField
              size="l"
              labelText={t('common:registration.select-signup-method.name-field-label')}
              inputId="name"
            >
              <TextInput
                size="l"
                id="name"
                name="name"
                autoFocus
                data-testid="name-input"
                placeholder={t('common:registration.select-signup-method.name-field-placeholder')}
                maxLength={50}
                isInvalid={!!errors.name}
                ref={register({
                  required: t('common:login.form.required') as string,
                })}
              />
              {!!errors.name && (
                <ErrorText data-testid="name-field-error">{errors.name.message}</ErrorText>
              )}
            </StyledField>
          </>
        )}
        <StyledField
          size="l"
          labelText={t('common:registration.from-invite.role-field-label')}
          inputId="role"
        >
          <Select
            id="role"
            name="role"
            data-testid="role-select"
            isInvalid={!!errors.role}
            ref={register({
              required: t('common:login.form.required') as string,
            })}
            size="l"
          >
            <option value="">{t(`common:registration.from-invite.role-field-empty-option`)}</option>
            {roles.map(({ value, label }) => (
              <option value={value} key={value}>
                {t(label)}
              </option>
            ))}
          </Select>
          {!!errors.role && (
            <ErrorText data-testid="role-select-error">{errors.role.message}</ErrorText>
          )}
        </StyledField>

        {!isAuthenticatedWithGoogle && (
          <Controller
            control={control}
            name="password"
            defaultValue=""
            rules={{ validate: () => isPasswordValid || 'error' }}
            render={({ onChange }) => (
              <PasswordInputWithValidation
                onChange={onChange}
                required={formState.isSubmitted}
                isPasswordValidCallback={(isValid) => setPasswordValidState(!!isValid)}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="terms"
          defaultValue={false}
          rules={{ required: t('common:registration.from-invite.accept-terms-error') as string }}
          render={({ onChange, value }) => (
            <StyledCheckbox
              id="terms"
              name="terms"
              isChecked={value}
              onChange={() => onChange(!value)}
              isInvalid={!!errors.terms}
              labelContent={
                <span id="termsLabel">
                  {t('common:registration.select-signup-method.terms-label')}
                  <TermsOfServiceLink
                    href="https://www.hotjar.com/legal/policies/terms-of-service/"
                    kind="inline"
                    isExternal
                    data-testid="terms-link"
                  >
                    {t('common:registration.select-signup-method.terms-link')}
                  </TermsOfServiceLink>
                </span>
              }
            />
          )}
        />
        {!!errors.terms && (
          <StyledErrorText data-testid="terms-field-error">{errors.terms.message}</StyledErrorText>
        )}
        <StyledButton
          id="submit-register-invite"
          data-testid="submit-register-invite"
          type="submit"
          kind="primary"
          isDisabled={isSubmitInProgress}
          size="l"
          isLoading={isSubmitInProgress}
        >
          {isSubmitInProgress
            ? t('common:registration.select-signup-method.submit-btn-loading')
            : t('common:registration.select-signup-method.submit-btn-text')}
        </StyledButton>
      </Form>
    </ColFormWrapper>
  );
};
